import { useCallback, useEffect, useRef, useState } from 'react';
import { useStopAgentMutation } from '@gonfalon/observability-graphql/hooks';
import type { z, ZodTypeAny } from 'zod';

import type { VegaConversationAck } from '../conversation/useVegaConversation';
import { useVegaConversation, VegaPollTimeoutError } from '../conversation/useVegaConversation';
import { toolProgressLabel } from '../internal/toolProgressLabel';

import type { AiCompletionError } from './useAiCompletionResult';

export type AgentRunAck = VegaConversationAck & { is_final?: boolean | null; text?: string | null };

export type AgentRunErrorCode = 'not_started' | 'run_failed';

export type AgentRunError =
  | AiCompletionError
  | { kind: 'timeout' }
  | { kind: 'limit'; message: string }
  | { kind: 'agent'; message: string; errorCode?: AgentRunErrorCode };

export interface UseAgentJsonResultOptions<TSchema extends ZodTypeAny> {
  schema: TSchema;
  start: (signal: AbortSignal) => Promise<AgentRunAck | null | undefined>;
  onSuccess: (result: z.infer<TSchema>) => void;
  onError?: (error: AgentRunError) => void;
  timeoutMs?: number;
}

export interface UseAgentJsonResultReturn {
  run: (opts?: { continuing?: boolean }) => Promise<void>;
  cancel: () => void;
  isRunning: boolean;
  progressLabel: string | null;
  error: AgentRunError | null;
}

type DeadlineResult<T> = { timedOut: true } | { timedOut: false; value: T };

function raceDeadline<T>(promise: Promise<T>, deadline: number | undefined): Promise<DeadlineResult<T>> {
  if (deadline === undefined) {
    return promise.then((value) => ({ timedOut: false, value }));
  }
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve({ timedOut: true }), Math.max(0, deadline - Date.now()));
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve({ timedOut: false, value });
      },
      (reason: unknown) => {
        clearTimeout(timer);
        reject(reason);
      },
    );
  });
}

export function useAgentJsonResult<TSchema extends ZodTypeAny>(
  options: UseAgentJsonResultOptions<TSchema>,
): UseAgentJsonResultReturn {
  const [isRunning, setIsRunning] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [error, setError] = useState<AgentRunError | null>(null);

  const { poll } = useVegaConversation();
  const [stopAgent] = useStopAgentMutation();

  const latestRef = useRef(options);
  useEffect(() => {
    latestRef.current = options;
  });

  const conversationIdRef = useRef<string | null>(null);
  const runConvRef = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef(0);

  const stopAgentBestEffort = useCallback(() => {
    const convId = conversationIdRef.current;
    if (convId) {
      stopAgent({ variables: { conversation_id: convId } }).catch(() => {});
    }
  }, [stopAgent]);

  const stopAgentBestEffortRef = useRef(stopAgentBestEffort);
  useEffect(() => {
    stopAgentBestEffortRef.current = stopAgentBestEffort;
  }, [stopAgentBestEffort]);
  useEffect(
    () => () => {
      runIdRef.current += 1;
      if (abortRef.current && !abortRef.current.signal.aborted) {
        abortRef.current.abort();
        stopAgentBestEffortRef.current();
      }
      conversationIdRef.current = null;
      runConvRef.current = null;
    },
    [],
  );

  const run = useCallback(
    async (opts?: { continuing?: boolean }) => {
      const runId = ++runIdRef.current;
      const supersededRunActive = !!abortRef.current && !abortRef.current.signal.aborted;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      if (!opts?.continuing) {
        if (supersededRunActive) {
          stopAgentBestEffort();
        }
        conversationIdRef.current = null;
      }
      runConvRef.current = opts?.continuing ? conversationIdRef.current : null;

      setIsRunning(true);
      setError(null);
      setProgressLabel(null);

      const { timeoutMs } = latestRef.current;
      const deadline = timeoutMs === undefined ? undefined : Date.now() + timeoutMs;

      const fail = (err: AgentRunError) => {
        if (runId !== runIdRef.current) {
          return;
        }
        abortRef.current = null;
        setIsRunning(false);
        setProgressLabel(null);
        setError(err);
        try {
          latestRef.current.onError?.(err);
        } catch {
          return;
        }
      };

      const startPromise = latestRef.current.start(controller.signal);
      let ack: AgentRunAck | null | undefined;
      try {
        const started = await raceDeadline(startPromise, deadline);
        if (started.timedOut) {
          controller.abort();
          startPromise
            .then((lateAck) => {
              if (lateAck?.conversation_id && !lateAck.is_final) {
                stopAgent({ variables: { conversation_id: lateAck.conversation_id } }).catch(() => {});
              }
            })
            .catch(() => {});
          fail({ kind: 'timeout' });
          return;
        }
        ack = started.value;
      } catch (startErr) {
        if (runId !== runIdRef.current || controller.signal.aborted) {
          return;
        }
        fail({ kind: 'fetch', error: startErr });
        return;
      }

      if (runId !== runIdRef.current) {
        if (ack?.conversation_id && !ack.is_final && ack.conversation_id !== runConvRef.current) {
          stopAgent({ variables: { conversation_id: ack.conversation_id } }).catch(() => {});
        }
        return;
      }
      if (ack?.is_final) {
        fail({ kind: 'limit', message: ack.text ?? '' });
        return;
      }
      if (!ack?.conversation_id) {
        fail({ kind: 'agent', message: '', errorCode: 'not_started' });
        return;
      }

      conversationIdRef.current = ack.conversation_id;

      let raw: string;
      try {
        raw = await poll(ack, controller.signal, {
          timeoutMs: deadline === undefined ? undefined : Math.max(0, deadline - Date.now()),
          waitForTerminalStatus: true,
          onProgress: (progress) => {
            if (runId !== runIdRef.current) {
              return;
            }
            setProgressLabel(
              progress.kind === 'tool' ? toolProgressLabel(progress.name) : truncateNarration(progress.text),
            );
          },
        });
      } catch (pollErr) {
        if (runId !== runIdRef.current || controller.signal.aborted) {
          return;
        }
        if (pollErr instanceof VegaPollTimeoutError) {
          stopAgentBestEffort();
          fail({ kind: 'timeout' });
          return;
        }
        fail({ kind: 'agent', message: pollErr instanceof Error ? pollErr.message : '', errorCode: 'run_failed' });
        return;
      }

      if (runId !== runIdRef.current) {
        return;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(extractJsonObject(raw));
      } catch (parseErr) {
        fail({
          kind: 'parse',
          error: parseErr instanceof SyntaxError ? parseErr : new SyntaxError(String(parseErr)),
          raw,
        });
        return;
      }

      const validated = latestRef.current.schema.safeParse(parsed);
      if (!validated.success) {
        fail({ kind: 'schema', issues: validated.error.issues });
        return;
      }

      abortRef.current = null;
      setIsRunning(false);
      setProgressLabel(null);
      latestRef.current.onSuccess(validated.data);
    },
    [poll, stopAgent, stopAgentBestEffort],
  );

  const cancel = useCallback(() => {
    runIdRef.current += 1;
    const inFlight = !!abortRef.current && !abortRef.current.signal.aborted;
    abortRef.current?.abort();
    abortRef.current = null;
    setIsRunning(false);
    setProgressLabel(null);
    if (inFlight) {
      stopAgentBestEffort();
    }
    setError(null);
    conversationIdRef.current = null;
    runConvRef.current = null;
  }, [stopAgentBestEffort]);

  return { run, cancel, isRunning, progressLabel, error };
}

export function extractJsonObject(text: string): string {
  let body = text.trim();
  if (body.startsWith('```')) {
    body = body
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/```$/, '')
      .trim();
  }

  const start = body.indexOf('{');
  if (start === -1) {
    return body;
  }

  let depth = 0;
  let inString = false;
  for (let i = start; i < body.length; i++) {
    const char = body[i];
    if (inString) {
      if (char === '\\') {
        i += 1;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }
    if (char === '"') {
      inString = true;
    } else if (char === '{') {
      depth += 1;
    } else if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        return body.slice(start, i + 1);
      }
    }
  }
  return body;
}

export function truncateNarration(text: string, maxLength = 90): string {
  const collapsed = text.replaceAll(/\s+/g, ' ').trim();
  if (collapsed.length <= maxLength) {
    return collapsed;
  }
  const cut = collapsed.slice(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > maxLength / 2 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
